/**
 * FmSection — the one section shell. Every section renders here: band tone,
 * padding and width through FmSectionBand, the section-level state, then the
 * arrangement placing its elements. Geometry is the arrangement's semantic
 * options resolved to the three split recipes and the grid column tokens.
 */
import React from 'react';
import { FmBanner } from '../FmBanner';
import { FmSpinner } from '../FmFeedback';
import { FmSectionBand, type FmSectionBandTone } from '../FmReportComposition';
import { FmActionButton, FmNoticeElementView } from '../elements/FmActionElements';
import { FmCopyElementView } from '../elements/FmTextElements';
import { keyedElements } from '../elements/elementKeys';
import { FmElementContextProvider, type FmElementContextValue } from '../elements/elementContext';
import { FmButton } from '../FmButton';
import { FmSectionArrangement } from './FmSectionArrangements';
import type {
  FmSection as FmSectionDescriptor,
  FmSectionState,
  FmSectionTone,
  FmSectionWidth,
} from './section.types';
import { validateSection } from './section.validation';
import './FmSection.css';

export interface FmSectionProps {
  section: FmSectionDescriptor;
  /**
   * `band` is the full shell. `embedded` places the section inside a host band
   * (a report chapter), which already owns tone, padding and width; the
   * section's tone then only tells its primitives which contrast they sit on.
   */
  presentation?: 'band' | 'embedded';
  /** Level of a copy heading in this section; element titles sit one below. */
  headingLevel?: 2 | 3 | 4;
  /**
   * Tone of the band hosting an embedded section. The host paints the band and
   * the embedded section paints none, so a named host tone wins over the
   * section's own tone: ink must match the surface it actually sits on.
   */
  hostTone?: FmSectionTone;
  /** A heading an earlier sibling already printed; a copy heading repeating it renders titleless. */
  printedHeading?: string;
}

type UnavailableState = Extract<
  FmSectionState,
  { status: 'loading' | 'empty' | 'locked' | 'error' }
>;
type NoticeState = Extract<FmSectionState, { status: 'partial' | 'stale' }>;

/**
 * `content` caps the rail at the content width; `wide` drops the cap and keeps
 * the gutters. `full` keeps the content rail for copy so it aligns with every
 * other band, while its visual runs past it: a band image spans the viewport
 * and a split's media column bleeds to the viewport edge.
 */
const RAIL: Record<FmSectionWidth, 'content' | 'wide'> = {
  content: 'content',
  wide: 'wide',
  full: 'content',
};

function isUnavailable(state: FmSectionState): state is UnavailableState {
  return state.status !== 'ready' && state.status !== 'partial' && state.status !== 'stale';
}

function stateAction(state: UnavailableState): React.ReactNode {
  if ((state.status === 'empty' || state.status === 'locked') && state.action) {
    return <FmActionButton action={state.action} variant="bordered" />;
  }
  if (state.status === 'error' && state.onRetry) {
    return (
      <FmButton variant="bordered" size="sm" onClick={state.onRetry}>
        {state.retryLabel}
      </FmButton>
    );
  }
  return undefined;
}

/**
 * Loading, empty, locked and error replace the content with one typed notice
 * at body scale, so it never outweighs the chapter or section heading above it.
 */
const SectionStateNotice: React.FC<{ state: UnavailableState; testId: string }> = ({
  state,
  testId,
}) => (
  <FmBanner
    status={state.status}
    title={state.label}
    description={state.status === 'loading' ? undefined : state.explanation}
    icon={state.status === 'loading' ? <FmSpinner size="sm" /> : undefined}
    action={stateAction(state)}
    data-testid={testId}
  />
);

/** Partial and stale keep the content and say what is missing or how old it is. */
const SectionContentNotice: React.FC<{ state: NoticeState; testId: string }> = ({
  state,
  testId,
}) => (
  <FmBanner
    status={state.status}
    title={state.label}
    description={state.explanation}
    timestamp={
      state.status === 'stale' ? `${state.observedAtLabel} ${state.observedAt}` : undefined
    }
    action={
      state.status === 'stale' && state.onRefresh ? (
        <FmButton variant="bordered" size="sm" onClick={state.onRefresh}>
          {state.refreshLabel}
        </FmButton>
      ) : undefined
    }
    data-testid={testId}
  >
    {state.status === 'partial' ? (
      <span className="fm-section__missing">
        {`${state.missingFieldsLabel}: ${state.missingFields.join(', ')}`}
      </span>
    ) : null}
  </FmBanner>
);

const SectionBody: React.FC<{
  section: FmSectionDescriptor;
  base: string;
  tone: FmSectionTone;
}> = ({ section, base, tone }) => {
  const { state } = section;
  return (
    <div
      className="fm-section"
      data-arrangement={section.arrangement}
      data-bleed={bleedsMedia(section) ? 'true' : undefined}
      data-tone={tone}
      data-state={state.status}
      aria-busy={state.status === 'loading' ? true : undefined}
    >
      {section.notices?.length ? (
        <div className="fm-section__notices">
          {keyedElements(section.notices).map(({ key, element }) => (
            <FmNoticeElementView key={key} element={element} />
          ))}
        </div>
      ) : null}
      {isUnavailable(state) ? (
        <>
          {'lead' in section && section.lead ? <FmCopyElementView element={section.lead} /> : null}
          <SectionStateNotice state={state} testId={`${base}-state`} />
        </>
      ) : (
        <>
          {state.status === 'ready' ? null : (
            <SectionContentNotice state={state} testId={`${base}-state`} />
          )}
          <FmSectionArrangement section={section} base={base} />
        </>
      )}
    </div>
  );
};

/**
 * A full-width split runs its media column out to the viewport edge on its
 * side; a band's cover photo spans the viewport even inside a chapter band.
 */
function bleedsMedia(section: FmSectionDescriptor): boolean {
  if (section.arrangement === 'split') return section.width === 'full';
  return (
    section.arrangement === 'band' &&
    section.width === 'full' &&
    'slots' in section &&
    Boolean(section.slots?.background) &&
    section.imageFit !== 'contain'
  );
}

/** A band with a background image reads on the deep band unless the route chose a tone. */
function bandTone(section: FmSectionDescriptor): Exclude<FmSectionBandTone, 'plain' | 'accent'> {
  if (section.tone) return section.tone;
  const hasBackground =
    section.arrangement === 'band' && 'slots' in section && Boolean(section.slots?.background);
  return hasBackground ? 'deep' : 'light';
}

export const FmSection: React.FC<FmSectionProps> = ({
  section,
  presentation = 'band',
  headingLevel = 2,
  hostTone,
  printedHeading,
}) => {
  validateSection(section);
  const base = section.testId ?? section.id;
  const tone = presentation === 'embedded' && hostTone ? hostTone : bandTone(section);
  const context: FmElementContextValue = {
    base,
    contrast: tone === 'light' || tone === 'paper' ? 'light' : 'dark',
    headingLevel,
    ...(printedHeading === undefined ? {} : { printedHeading }),
  };
  const body = (
    <FmElementContextProvider value={context}>
      <SectionBody section={section} base={base} tone={tone} />
    </FmElementContextProvider>
  );
  if (presentation === 'embedded') {
    return (
      <article
        id={section.id}
        className="fm-section-embedded"
        aria-label={printedHeading}
        data-testid={base}
      >
        {body}
      </article>
    );
  }
  return (
    <FmSectionBand
      id={section.id}
      tone={tone}
      density={section.density ?? 'comfortable'}
      width={section.width === 'content' || section.width === undefined ? 'content' : 'full'}
      rail={RAIL[section.width ?? 'content']}
      className="fm-section-shell"
      data-testid={base}
    >
      {body}
    </FmSectionBand>
  );
};

FmSection.displayName = 'FmSection';
